// Entry Actions Handler - Handles UI-related effects
// Maps entry action keys to concrete effects without adding new network code

import { DialogueEffect, DialogueEvent } from './types';
import { EffectHandler } from './EffectBus';
import { t, Lang } from '../../../../i18n';
import { sqlApi, serializeConversationForStorage } from '../../../../services/sqlApi';

export class EntryActionsHandler implements EffectHandler {
  private conversation: any;
  private triggerConversationUpdate: () => void;
  private getCurrentLanguage: () => Lang;
  private onNewConversationRequested?: () => void;
  private currentUser?: string;
  private dispatchEvent?: (event: DialogueEvent) => void;

  constructor(
    conversation: any,
    triggerConversationUpdate: () => void,
    getCurrentLanguage: () => Lang,
    onNewConversationRequested?: () => void,
    currentUser?: string,
    dispatchEvent?: (event: DialogueEvent) => void
  ) {
    this.conversation = conversation;
    this.triggerConversationUpdate = triggerConversationUpdate;
    this.getCurrentLanguage = getCurrentLanguage;
    this.onNewConversationRequested = onNewConversationRequested;
    this.currentUser = currentUser;
    this.dispatchEvent = dispatchEvent;
  }

  canHandle(effect: DialogueEffect): boolean {
    return effect.type === 'RUN_ENTRY_ACTION';
  }

  async handle(effect: DialogueEffect): Promise<void> {
    if (effect.type !== 'RUN_ENTRY_ACTION') return;

    const { key, params } = effect;
    
    // Map action keys to concrete effects by directly adding messages to conversation
    switch (key) {
      case 'greet_user':
        this.conversation.addSystemMessage(t(this.getCurrentLanguage(), 'conv_welcome'), 'conv_welcome');
        this.triggerConversationUpdate();
        break;
        
      case 'provide_help':
        this.conversation.addSystemMessage(t(this.getCurrentLanguage(), 'conv_help'), 'conv_help');
        this.triggerConversationUpdate();
        break;
        
      case 'ask_task_description':
        this.conversation.addSystemMessage(t(this.getCurrentLanguage(), 'conv_task_request'), 'conv_task_request');
        this.triggerConversationUpdate();
        break;
        
      case 'ask_options':
        // Find the first task that isn't being processed yet and mark it as active
        if (!this.conversation.tasks || this.conversation.tasks.length === 0) {
          break;
        }
        
        // Mark the first unprocessed task as active
        const firstUnprocessedTask = this.conversation.tasks.find((t: any) => 
          !t.isProcessingDialogue && 
          !t.isSubmitted &&
          t.dialogueState !== 'task_completed' &&
          t.dialogueState !== 'task_aborted'
        );
        
        if (!firstUnprocessedTask) {
          break;
        }
        
        // Mark this task as active for processing
        firstUnprocessedTask.isProcessingDialogue = true;
        firstUnprocessedTask.dialogueState = 'processing_options_selection';
        
        // Kick off task processing using existing helper (calls FastAPI)
        // This will add the "Processing your tasks..." message and make the API call
        try {
          const mod = await import('../ChatPerDialogueStepHandlers/Handle_conv_task_processing_processing_options_selection');
          await mod.handleConvTaskProcessingProcessingOptionsSelection(this.conversation, this.triggerConversationUpdate);
        } catch (err) {
          // Failed to start task processing
        }
        break;
        
      case 'ask_variant':
        // Find the active task and get its selected task's variants
        const activeTask = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
        if (!activeTask) {
          break;
        }
        
        // First, try to get selectedTask directly
        let selectedTask = activeTask?.selectedTask;
        
        // If selectedTask doesn't exist yet, try to find it from top_k_tasks using selectedTaskId
        if (!selectedTask && activeTask.selectedTaskId && activeTask.top_k_tasks) {
          selectedTask = activeTask.top_k_tasks.find(
            (t: any) => t.task_id === activeTask.selectedTaskId
          );
          if (selectedTask) {
            // Set it on the task object for future reference
            activeTask.selectedTask = selectedTask;
          }
        }
        
        // Variants can be in multiple places:
        // 1. selectedTask.variants (from top_k_tasks response)
        // 2. selectedTask.extracted_values (alternative name)
        // 3. activeTask.extractedValues (from processing response)
        const variants = selectedTask?.variants || 
                        selectedTask?.extracted_values || 
                        activeTask?.extractedValues || 
                        {};
        
        // VariantSelectorMessage expects 'variants' as an object (not variantOptions array)
        // The object structure should be: { "variantId": { ...variantData }, ... }
        // Add variant selection message with variants object
        const variantMessage2 = {
          id: Math.random().toString(36).substr(2, 9),
          text: t(this.getCurrentLanguage(), 'variant_selection'),
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'variantSelection',
          variants: variants  // Use 'variants' (object) not 'variantOptions' (array)
        };
        this.conversation.messages.push(variantMessage2);
        this.triggerConversationUpdate();
        break;
        
      case 'ask_amount':
        // Get the active task to find the billing unit
        const activeTaskForAmount = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
        
        // Helper function to get billing unit
        const getBillingUnit = (selectedTask: any, taskId?: string): string => {
          if (!selectedTask?.billing) return '';
          
          // Try to get billing info by task_id
          let billing = taskId ? selectedTask.billing[taskId] : null;
          
          // If not found, try to get the first billing entry
          if (!billing && Object.keys(selectedTask.billing).length > 0) {
            const firstBillingKey = Object.keys(selectedTask.billing)[0];
            billing = selectedTask.billing[firstBillingKey];
          }
          
          return billing?.billable_unit || '';
        };
        
        // Get billing unit from active task
        let unit = '';
        if (activeTaskForAmount?.selectedTask) {
          unit = getBillingUnit(
            activeTaskForAmount.selectedTask, 
            activeTaskForAmount.selectedTask?.task_id || activeTaskForAmount.selectedTaskId
          );
        }
        
        // Build the message with unit if available
        let amountQuestion = t(this.getCurrentLanguage(), 'amount_clarification');
        if (unit) {
          amountQuestion = `${amountQuestion} (${unit})`;
        }
        
        this.conversation.addSystemMessage(amountQuestion, 'ask_amount');
        this.triggerConversationUpdate();
        break;

      case 'confirm_review':
        this.conversation.addSystemMessage(t(this.getCurrentLanguage(), 'final_confirmation'), 'ask_confirmation');
        this.triggerConversationUpdate();
        break;
        
      case 'confirm_abort':
        this.conversation.addSystemMessage(t(this.getCurrentLanguage(), 'abort_confirmation'), 'ask_confirmation');
        this.triggerConversationUpdate();
        break;
        
      case 'show_task_summary':
        // Find all completed/submitted tasks
        const completedTasks = this.conversation.tasks?.filter((t: any) => 
          t.dialogueState === 'task_completed' || 
          t.dialogueState === 'defined_task_completed' ||
          t.isSubmitted || 
          t.status === 'completed'
        ) || [];
        
        
        // Map tasks to the format expected by TaskSummaryTable
        // In the new architecture, each subtask is its own task object
        const mappedTasks: any[] = [];
        
        for (const task of completedTasks) {
          // NEW ARCHITECTURE: Each subtask is its own task object
          // Check if this is a well-defined task subtask (has wellDefinedTaskId)
          // CRITICAL: Skip parent tasks where taskId === wellDefinedTaskId (these are placeholder tasks, not actual subtasks)
          if (task.wellDefinedTaskId && task.taskId === task.wellDefinedTaskId) {
            // This is a parent task placeholder - skip it (only show actual subtasks)
            continue;
          }
          if (task.wellDefinedTaskId) {
            // Get subtask descriptions from well-defined tasks structure
            let subtaskDescriptions: Record<string, string> = {};
            try {
              const { getSubtasksForTask } = await import('../DialogueManagementDefinedTaskClassification/wellDefinedTasks');
              const lang = this.getCurrentLanguage();
              const subtasks = getSubtasksForTask(task.wellDefinedTaskId, lang);
              subtasks.forEach((s: any) => {
                subtaskDescriptions[s.id] = s.name || s.description || s.id;
              });
            } catch (err) {
              // Could not get subtask descriptions
            }
            
            // In the new architecture, each task object represents one subtask
            // Use taskId - THE SINGLE NUMERICAL ID that gets updated as we navigate deeper
            // Prefer task.taskId (actual task/subtask ID) over task.id (UUID) for display
            const displayId = task.taskId || task.id;
            // For ID column, always prefer task.taskId (numeric ID) over task.id (UUID)
            // CRITICAL: Never use task.id (UUID) or wellDefinedTaskId (parent ID) for the ID column
            // task.taskId might be set to wellDefinedTaskId initially (e.g. "property_connection"), but should be updated to subtask ID
            let idForColumn = task.taskId;
            const isUUID = (s: string) => /^[a-z0-9]{8,}$/i.test(s) && s.length >= 8 && !/^\d+$/.test(s);
            const isWellDefinedTaskId = (s: string) => s && (s === task.wellDefinedTaskId || s.startsWith('property_') || s.startsWith('manhole_'));
            
            // Check if taskId is missing, equals UUID, equals wellDefinedTaskId (parent), or is invalid
            const idForColumnStr = String(idForColumn || '');
            const taskIdStr = String(task.id || '');
            const isTaskIdUUID = isUUID(idForColumnStr) || idForColumnStr === taskIdStr;
            const isTaskIdParent = isWellDefinedTaskId(idForColumnStr);
            
            if (!idForColumn || isTaskIdUUID || isTaskIdParent) {
              // Debug log to understand what's happening
              if (isTaskIdUUID || isTaskIdParent) {
                console.log('🔍 [EntryActionsHandler] Task taskId is UUID or parent ID, searching for valid ID:', {
                  taskId: task.id,
                  taskTaskId: task.taskId,
                  isUUID: isTaskIdUUID,
                  isParent: isTaskIdParent,
                  wellDefinedTaskId: task.wellDefinedTaskId,
                  selectedVariantId: task.selectedVariantId,
                  selectedTaskTaskId: task.selectedTask?.task_id
                });
              }
              // Try to find the actual task ID from other fields (prefer numeric/subtask IDs)
              const candidates = [
                task.selectedVariantId, // Final variant/child ID after selection
                task.selectedTask?.task_id, // From selectedTask object
                task.finalTaskId, // Final task ID if set
                task.classifiedTaskId // Classified task ID if set
              ].filter(id => {
                if (!id) return false;
                const idStr = String(id);
                // Reject UUIDs, wellDefinedTaskIds, and the task.id UUID
                if (idStr === String(task.id) || isUUID(idStr) || isWellDefinedTaskId(idStr)) return false;
                // Accept numeric IDs or IDs that look like subtask/variant IDs (alphanumeric, reasonable length)
                return idStr.length >= 3 && idStr.length <= 20 && /^[\dA-Za-z_-]+$/.test(idStr);
              });
              
              if (candidates.length > 0) {
                idForColumn = candidates[0];
              } else {
                // Last resort: log warning with full task details for debugging
                console.warn('⚠️ [EntryActionsHandler] Task missing valid taskId, cannot determine ID for column:', {
                  taskId: task.id,
                  taskTaskId: task.taskId,
                  taskTaskIdType: typeof task.taskId,
                  hasSelectedVariantId: !!task.selectedVariantId,
                  selectedVariantIdValue: task.selectedVariantId,
                  hasSelectedTask: !!task.selectedTask,
                  selectedTaskTaskId: task.selectedTask?.task_id,
                  wellDefinedTaskId: task.wellDefinedTaskId,
                  finalTaskId: task.finalTaskId,
                  classifiedTaskId: task.classifiedTaskId,
                  taskObject: JSON.stringify(task, null, 2)
                });
                // Use a placeholder instead of UUID - this should never happen in normal flow
                idForColumn = 'N/A';
              }
            }
            
            // Get description for this subtask
            let subtaskDescription = subtaskDescriptions[displayId] || 
                                    subtaskDescriptions[task.taskId] ||
                                    task.userDescription ||
                                    displayId;
            
            // If we have a variant ID, try to get variant description from messages
            if (task.selectedVariantId && !subtaskDescription) {
              const variantMessages = this.conversation.messages.filter((msg: any) => 
                msg.messageType === 'variantSelection' && msg.variants && msg.variants[task.selectedVariantId]
              );
              
              if (variantMessages.length > 0) {
                const variantObj = variantMessages[0].variants[task.selectedVariantId];
                if (variantObj) {
                  const billingFieldsToExclude = ['billable_unit', 'quantity_result_obligation', 'price_per_unit_in_euro'];
                  const formatMeasurement = (key: string, value: any): string => {
                    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
                      try {
                        const parsed = JSON.parse(value[0]);
                        return formatMeasurement(key, parsed);
                      } catch (e) {
                        return `${key}: ${value[0]}`;
                      }
                    }
                    if (key === 'size' && typeof value === 'object' && value !== null && Array.isArray(value.values) && value.unit) {
                      return `${key}: ${value.values.join(' x ')} ${value.unit}`;
                    }
                    if (['area', 'height', 'diameter', 'depth', 'length'].includes(key) && typeof value === 'object' && value !== null) {
                      if (value.values && Array.isArray(value.values) && value.unit) {
                        return `${key}: ${value.values.join(' x ')} ${value.unit}`;
                      }
                      if (value.value !== undefined && value.unit) {
                        return `${key}: ${value.value} ${value.unit}`;
                      }
                    }
                    if (typeof value === 'object' && value !== null) {
                      return `${key}: ${JSON.stringify(value)}`;
                    }
                    return `${key}: ${String(value)}`;
                  };
                  
                  const variantLabel = Object.entries(variantObj)
                    .filter(([k]) => !billingFieldsToExclude.includes(k))
                    .map(([k, v]) => formatMeasurement(k, v))
                    .filter(Boolean)
                    .join(', ');
                  
                  if (variantLabel) {
                    const baseDescription = subtaskDescriptions[task.taskId] || '';
                    subtaskDescription = baseDescription 
                      ? `${baseDescription} (${variantLabel})`
                      : variantLabel;
                  }
                }
              }
            }
            
            // Get unit from task object
            let unit = task.unit || task.billableUnit || '';
            
            // Try to get unit from selectedTask billing if available
            if (!unit && task.selectedTask?.billing) {
              const idsToTry = [task.selectedVariantId, task.taskId, displayId].filter(Boolean);
              for (const idToTry of idsToTry) {
                if (task.selectedTask.billing[idToTry]?.billable_unit) {
                  unit = task.selectedTask.billing[idToTry].billable_unit;
                  break;
                }
              }
            }
            
            // Build billableAmount
            let billableAmount: { amount: number; unit: string } | undefined;
            if (task.amount !== undefined && task.amount !== null) {
              billableAmount = {
                amount: typeof task.amount === 'string' ? parseFloat(task.amount) : task.amount,
                unit: unit || ''
              };
            }
            
            // Map this task object directly to a summary row
            mappedTasks.push({
              id: task.id,
              userDescription: subtaskDescription,
              description: subtaskDescription,
              selectedTask: { 
                description: subtaskDescription,
                task_id: displayId 
              },
              selectedVariantId: idForColumn, // Use task.taskId (numeric ID) instead of task.id (UUID)
              billableAmount: billableAmount,
              employeeCount: 1, // Default to 1
              location: task.location || null
            });
          } else {
            // OLD ARCHITECTURE: Check if this is a well-defined task with processed subtasks
            if (task.processedSubtasks && task.processedSubtasks.length > 0) {
              // Get subtask descriptions from well-defined tasks structure
              let subtaskDescriptions: Record<string, string> = {};
              if (task.wellDefinedTaskId) {
                try {
                  const { getSubtasksForTask } = await import('../DialogueManagementDefinedTaskClassification/wellDefinedTasks');
                  const lang = this.getCurrentLanguage();
                  const subtasks = getSubtasksForTask(task.wellDefinedTaskId, lang);
                  subtaskDescriptions = {};
                  subtasks.forEach((s: any) => {
                    subtaskDescriptions[s.id] = s.name || s.description || s.id;
                  });
                } catch (err) {
                  // Could not get subtask descriptions
                }
              }
              
              // Create a row for each processed subtask
              task.processedSubtasks.forEach((processedSubtask: any) => {
                // Determine the ID to use: variantId > childId > subtaskId
                const displayId = processedSubtask.variantId || 
                                 processedSubtask.childId || 
                                 processedSubtask.subtaskId;
                // For the ID column, prefer numeric task/subtask IDs (e.g. "184110") over UUIDs (e.g. "ueqrqxo8c").
                // variantId can be a description (e.g. "height: 1.!") or a UUID; always prefer subtaskId/childId when available.
                const looksLikeNumericId = (s: string) => /^\d+$/.test(s || '');
                const idForColumn = processedSubtask.subtaskId || 
                                   processedSubtask.childId || 
                                   (displayId && looksLikeNumericId(displayId) ? displayId : null) ||
                                   displayId;
                
                // Try multiple ways to find the description:
                // 1. By subtaskId
                // 2. By childId
                // 3. By variantId (if it's a base subtask ID, not a variant)
                // 4. Fallback to displayId if nothing found
                let subtaskDescription = subtaskDescriptions[processedSubtask.subtaskId] || 
                                        subtaskDescriptions[processedSubtask.childId] ||
                                        subtaskDescriptions[processedSubtask.variantId] ||
                                        subtaskDescriptions[displayId];
                
                // If still not found and displayId looks like a subtask ID (not a variant), try to extract base ID
                if (!subtaskDescription && displayId) {
                  // Try to find by matching the start of the ID (e.g., "151050" -> "151")
                  const baseId = displayId.toString().replace(/\d+$/, '').replace(/XX$/, '');
                  if (baseId && baseId !== displayId) {
                    subtaskDescription = subtaskDescriptions[baseId];
                  }
                }
                
                // If still not found and we have a variantId, try to get description from variant selection messages
                if (!subtaskDescription && processedSubtask.variantId) {
                  // Look for variant selection messages in the conversation
                  const variantMessages = this.conversation.messages.filter((msg: any) => 
                    msg.messageType === 'variantSelection' && msg.variants && msg.variants[processedSubtask.variantId]
                  );
                  
                  if (variantMessages.length > 0) {
                    const variantObj = variantMessages[0].variants[processedSubtask.variantId];
                    if (variantObj) {
                      // Format the variant description from measurements (same as in VariantSelectorMessage)
                      const billingFieldsToExclude = ['billable_unit', 'quantity_result_obligation', 'price_per_unit_in_euro'];
                      const formatMeasurement = (key: string, value: any): string => {
                        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
                          try {
                            const parsed = JSON.parse(value[0]);
                            return formatMeasurement(key, parsed);
                          } catch (e) {
                            return `${key}: ${value[0]}`;
                          }
                        }
                        if (key === 'size' && typeof value === 'object' && value !== null && Array.isArray(value.values) && value.unit) {
                          return `${key}: ${value.values.join(' x ')} ${value.unit}`;
                        }
                        if (['area', 'height', 'diameter', 'depth', 'length'].includes(key) && typeof value === 'object' && value !== null) {
                          if (value.values && Array.isArray(value.values) && value.unit) {
                            return `${key}: ${value.values.join(' x ')} ${value.unit}`;
                          }
                          if (value.value !== undefined && value.unit) {
                            return `${key}: ${value.value} ${value.unit}`;
                          }
                        }
                        if (typeof value === 'object' && value !== null) {
                          return `${key}: ${JSON.stringify(value)}`;
                        }
                        return `${key}: ${String(value)}`;
                      };
                      
                      const variantLabel = Object.entries(variantObj)
                        .filter(([k]) => !billingFieldsToExclude.includes(k))
                        .map(([k, v]) => formatMeasurement(k, v))
                        .filter(Boolean)
                        .join(', ');
                      
                      if (variantLabel) {
                        // Combine base subtask description with variant details
                        const baseDescription = subtaskDescriptions[processedSubtask.subtaskId] || 
                                              subtaskDescriptions[processedSubtask.childId] ||
                                              '';
                        subtaskDescription = baseDescription 
                          ? `${baseDescription} (${variantLabel})`
                          : variantLabel;
                      }
                    }
                  }
                }
                
                // Final fallback
                if (!subtaskDescription) {
                  subtaskDescription = displayId;
                }
                
                // Build billableAmount from subtask data
                // Try to get unit from multiple sources
                let unit = processedSubtask.unit || '';
                
                // If unit is not in processedSubtask, try to get it from the task's selectedTask billing
                if (!unit && task.selectedTask?.billing) {
                  // Try variantId first
                  if (processedSubtask.variantId && task.selectedTask.billing[processedSubtask.variantId]) {
                    unit = task.selectedTask.billing[processedSubtask.variantId].billable_unit || '';
                  }
                  // Try childId
                  if (!unit && processedSubtask.childId && task.selectedTask.billing[processedSubtask.childId]) {
                    unit = task.selectedTask.billing[processedSubtask.childId].billable_unit || '';
                  }
                  // Try subtaskId
                  if (!unit && processedSubtask.subtaskId && task.selectedTask.billing[processedSubtask.subtaskId]) {
                    unit = task.selectedTask.billing[processedSubtask.subtaskId].billable_unit || '';
                  }
                }
                
                // Also check task.subtaskData.processedSubtasks as a fallback
                if (!unit && task.subtaskData?.processedSubtasks) {
                  const subtaskDataEntry = task.subtaskData.processedSubtasks.find(
                    (s: any) => s.variantId === displayId || s.subtaskId === displayId || s.childId === displayId ||
                                s.variantId === processedSubtask.variantId || 
                                s.subtaskId === processedSubtask.subtaskId ||
                                s.childId === processedSubtask.childId
                  );
                  if (subtaskDataEntry?.unit) {
                    unit = subtaskDataEntry.unit;
                  }
                }
                
                // If still no unit, try to fetch from backend (async, but we'll try)
                // Note: This is a fallback - ideally the unit should already be stored
                if (!unit && displayId && task.selectedTask?.billing) {
                  // Try all possible IDs
                  const idsToTry = [
                    processedSubtask.variantId,
                    processedSubtask.childId,
                    processedSubtask.subtaskId,
                    displayId
                  ].filter(Boolean);
                  
                  for (const idToTry of idsToTry) {
                    if (task.selectedTask.billing[idToTry]?.billable_unit) {
                      unit = task.selectedTask.billing[idToTry].billable_unit;
                      break;
                    }
                  }
                }
                
                let billableAmount: { amount: number; unit: string } | undefined;
                // Always create billableAmount if we have an amount, even if unit is missing
                if (processedSubtask.amount !== undefined && processedSubtask.amount !== null) {
                  billableAmount = {
                    amount: processedSubtask.amount,
                    unit: unit || '' // Allow empty unit if not found
                  };
                }
                
                mappedTasks.push({
                  id: `${task.id}-${displayId}`, // Unique ID for each subtask row
                  userDescription: subtaskDescription,
                  description: subtaskDescription,
                  selectedTask: { 
                    description: subtaskDescription,
                    task_id: displayId 
                  }, // For Task Description column
                  selectedVariantId: idForColumn, // For ID column - use proper ID, not variant description (e.g. "height: 1.!")
                  billableAmount: billableAmount, // For b. unit and b. amount columns
                  employeeCount: 1, // Default to 1
                  location: task.location || null // Shared location for all subtasks
                });
            });
          } else {
            // Standard task - map as before
            // Get variant info from selectedTask if available
            const selectedVariant = task.selectedTask?.variants?.[task.selectedVariantId] || 
                                   task.selectedTask?.extracted_values?.[task.selectedVariantId] ||
                                   task.extractedValues?.[task.selectedVariantId];
            
            // Build billableAmount from user-provided amount and unit from variant/backend
            let billableAmount: { amount: number; unit: string } | undefined;
            
            // If billableAmount already exists on the task object, use it
            if (task.billableAmount && task.billableAmount.unit) {
              billableAmount = task.billableAmount;
            } else if (task.amount !== undefined && task.amount !== null) {
              // Helper function to get billing information from selectedTask
              const getBillingUnit = (selectedTask: any, taskId?: string): string => {
                if (!selectedTask?.billing) return '';
                
                // Try to get billing info by task_id
                let billing = taskId ? selectedTask.billing[taskId] : null;
                
                // If not found, try to get the first billing entry
                if (!billing && Object.keys(selectedTask.billing).length > 0) {
                  const firstBillingKey = Object.keys(selectedTask.billing)[0];
                  billing = selectedTask.billing[firstBillingKey];
                }
                
                return billing?.billable_unit || '';
              };
              
              // Try to get unit from multiple sources, in order of preference:
              // 1. Variant unit
              // 2. Billing unit from selectedTask.billing[task_id].billable_unit
              // 3. Billing unit from task.billing[task_id].billable_unit
              // 4. Direct unit property on task
              const unit = selectedVariant?.unit || 
                          getBillingUnit(task.selectedTask, task.selectedTask?.task_id || task.selectedTaskId) ||
                          getBillingUnit({ billing: task.billing }, task.selectedTaskId) ||
                          task.selectedTask?.unit || 
                          task.unit ||
                          '';
              
              billableAmount = {
                amount: task.amount,
                unit: unit
              };
            }
            
            // Get employeeCount from task (set during splitting or from backend), default to 1
            const employeeCount = task.employeeCount || 
                                 task.selectedTask?.employee_count || 
                                 1; // Default to 1
            
            // Get selectedVariantId
            const selectedVariantId = task.selectedVariantId || null;
            
            // Get location from user input
            const location = task.location || null;
            
            // Get the correct description based on language - use dutch_description if available
            const lang = this.getCurrentLanguage();
            const taskDescription = lang === 'nl' 
              ? ((task.selectedTask as any)?.dutch_description || task.selectedTask?.description || task.description || '')
              : (task.selectedTask?.description || task.description || '');
            
            // Ensure selectedTask has the correct description
            const selectedTaskWithCorrectDescription = task.selectedTask ? {
              ...task.selectedTask,
              description: taskDescription
            } : {
              description: taskDescription,
              task_id: task.selectedTaskId || task.taskId || '',
              similarity: task.selectedTask?.similarity || 0
            };
            
            mappedTasks.push({
              id: task.id,
              userDescription: task.userDescription,
              description: taskDescription, // Use language-appropriate description
              dutchDescription: (task.selectedTask as any)?.dutch_description || task.description || '', // Store Dutch description separately
              selectedTask: selectedTaskWithCorrectDescription, // For Task Description column - with correct language
              selectedVariantId: selectedVariantId, // For ID column
              billableAmount: billableAmount, // For b. unit and b. amount columns
              employeeCount: employeeCount, // For Employees column
              location: location, // For Location column
              finalName: (task as any).finalName || task.name || task.userDescription // Final accumulated name path
            });
          }
          }
        }
        
        // Filter out parent tasks - a parent task is one whose ID is a prefix of other task IDs
        // Only include leaf tasks (tasks that don't have children with longer IDs)
        const filteredTasks = mappedTasks.filter((task: any) => {
          const taskId = task.selectedVariantId || task.selectedTask?.task_id || task.taskId || '';
          
          // If taskId is empty or invalid, keep it (might be a special case)
          if (!taskId || taskId === 'null' || taskId === 'undefined') {
            return true;
          }
          
          // Check if this taskId is a prefix of any other taskId in the mappedTasks
          // If it is, it's a parent task and should be filtered out
          const isParentTask = mappedTasks.some((otherTask: any) => {
            const otherTaskId = otherTask.selectedVariantId || otherTask.selectedTask?.task_id || otherTask.taskId || '';
            
            // Skip comparing with itself
            if (taskId === otherTaskId) {
              return false;
            }
            
            // Check if taskId is a prefix of otherTaskId (e.g., "184" is prefix of "184110")
            // Only consider it a parent if the other taskId is longer
            if (otherTaskId.length > taskId.length && otherTaskId.startsWith(taskId)) {
              return true; // This task is a parent of otherTask
            }
            
            return false;
          });
          
          // Keep the task only if it's NOT a parent task
          return !isParentTask;
        });
        
        // Show task summary message if there are completed tasks (after filtering)
        if (filteredTasks.length > 0) {
          // First, create an instruction message
          const instructionMessage = {
            id: Math.random().toString(36).substr(2, 9),
            text: '',
            textKey: 'reviewCompletedTasks',
            sender: 'Assistant',
            timestamp: new Date(),
            isAssistant: true,
            messageType: 'assistant' as const
          };
          this.conversation.messages.push(instructionMessage);
          
          // Then, create the editable task summary message (table in separate message)
          const summaryMessage = {
            id: Math.random().toString(36).substr(2, 9),
            text: '',
            sender: 'Assistant',
            timestamp: new Date(),
            isAssistant: true,
            messageType: 'task_summary' as const,
            taskData: filteredTasks, // Use filtered tasks (parent tasks removed)
            isEditable: true
          };
          this.conversation.messages.push(summaryMessage);
        }
        
        this.triggerConversationUpdate();
        break;
        
      case 'append_assignement_comment': {
        const text = (effect as { params?: { text?: string } }).params?.text;
        const aid = this.conversation?.assignementId;
        if (text?.trim() && aid != null) {
          try {
            await sqlApi.updateAssignement(aid, { append_comment: text.trim() });
          } catch (e) {
            console.warn('append_assignement_comment failed:', e);
          }
        }
        this.triggerConversationUpdate();
        break;
      }

      case 'submit_conversation':
        if (this.conversation) {
          // CRITICAL: Check if this conversation was ALREADY submitted before processing
          // If it was already submitted, this is an old conversation being loaded, not a fresh submission
          // Note: We only check isSubmitted, not the state, because the state might be set by the reducer
          // before this entry action runs, even for fresh submissions
          const wasAlreadySubmitted = this.conversation.isSubmitted;
          
          // Step 1: Get confirmed tasks from task summary message(s)
          // The task summary message contains the final list of tasks after user edits/deletions
          // This is the source of truth for what should be submitted
          const taskSummaryMessages = this.conversation.messages?.filter((msg: any) => 
            msg.messageType === 'task_summary' && msg.taskData && Array.isArray(msg.taskData) && msg.taskData.length > 0
          ) || [];
          
          // Get the most recent task summary message (in case there are multiple)
          // The taskData in the message is the final confirmed list after user edits
          const latestTaskSummary = taskSummaryMessages[taskSummaryMessages.length - 1];
          
          let confirmedTaskData: any[] = [];
          if (latestTaskSummary && latestTaskSummary.taskData) {
            confirmedTaskData = latestTaskSummary.taskData;
          } else {
            // Fallback: use tasks from conversation if no task summary found
            confirmedTaskData = this.conversation.tasks?.filter((task: any) => {
              const hasTaskCode = !!(task.finalTaskId || task.selectedTask?.task_id || task.classifiedTaskId || task.wellDefinedTaskId);
              const isCompleted = task.dialogueState === 'task_completed' || task.dialogueState === 'defined_task_completed';
              return (isCompleted || hasTaskCode) && !task.isSubmitted;
            }) || [];
          }
          
          // Mark corresponding tasks in conversation as submitted
          if (confirmedTaskData.length > 0) {
            confirmedTaskData.forEach((taskData: any) => {
              // Find the corresponding task in conversation.tasks by ID
              const task = this.conversation.tasks?.find((t: any) => {
                // Handle composite IDs (for subtasks: "taskId-subtaskId")
                if (taskData.id.includes('-')) {
                  const [taskId] = taskData.id.split('-');
                  return t.id === taskId;
                }
                return t.id === taskData.id;
              });
              
              if (task) {
                task.isSubmitted = true;
              }
            });
          }
          
          // Step 2: Mark conversation as submitted (only if not already submitted)
          if (!wasAlreadySubmitted) {
            if (this.conversation.markAsSubmitted) {
              this.conversation.markAsSubmitted();
            } else {
              // Fallback if markAsSubmitted doesn't exist
              this.conversation.isSubmitted = true;
            }
            
            // Step 2b: Submit tasks to database (only for fresh submissions)
            if (confirmedTaskData.length > 0 && this.currentUser) {
              try {
                const lang = this.getCurrentLanguage();
                // Get employee_id from username
                const employees = await sqlApi.getAllEmployees();
                
                const usernameParts = this.currentUser.split('_');
                const employee = employees.find((emp: any) => 
                  emp.name.toLowerCase() === usernameParts[0]?.toLowerCase() &&
                  emp.surname.toLowerCase() === usernameParts.slice(1).join('_').toLowerCase()
                );
                
                if (!employee) {
                  this.conversation.addSystemMessage(
                    lang === 'nl' 
                      ? "Fout: Werknemer niet gevonden. Taken zijn niet opgeslagen."
                      : "Error: Employee not found. Tasks were not saved."
                  );
                } else {
                  // Submit each confirmed task from the task summary to the database
                  const submissionPromises = confirmedTaskData.map(async (taskDataFromSummary: any, index: number) => {
                    try {
                      // Get task_code from taskData (this is the confirmed task from the summary)
                      // The taskData from summary has selectedTask.task_id which is the task_code
                      const taskCode = taskDataFromSummary.selectedTask?.task_id;
                      
                      if (!taskCode) {
                        return null;
                      }
                      
                      // Get quantity from billableAmount
                      let quantity = 1; // Default to 1 if not specified
                      if (taskDataFromSummary.billableAmount?.amount) {
                        quantity = taskDataFromSummary.billableAmount.amount;
                      }
                      
                      // Ensure quantity is a valid number
                      if (isNaN(quantity) || quantity <= 0) {
                        quantity = 1;
                      }
                      
                      // Use finalName in comments if available, otherwise use task description
                      const finalName = taskDataFromSummary.finalName || taskDataFromSummary.userDescription || '';
                      const taskData = {
                        task_code: taskCode,
                        quantity: Math.round(quantity * 100) / 100, // Round to 2 decimal places
                        employee_id: employee.id,
                        location:
                          taskDataFromSummary.location ||
                          this.conversation.assignementAddress ||
                          null,
                        forced_submit: false, // Default to false
                        comments: finalName || null, // Store final accumulated name path in comments
                        assignement_id: this.conversation.assignementId ?? null,
                      };
                      
                      const result = await sqlApi.addTask(taskData);
                      return result;
                    } catch (error) {
                      return null;
                    }
                  });
                  
                  const results = await Promise.all(submissionPromises);
                  const successCount = results.filter(r => r !== null).length;
                  const failCount = results.length - successCount;
                  
                  if (failCount > 0) {
                    this.conversation.addSystemMessage(
                      lang === 'nl' 
                        ? `Waarschuwing: ${successCount} taak(en) opgeslagen, ${failCount} taak(en) mislukt.`
                        : `Warning: ${successCount} task(s) saved, ${failCount} task(s) failed.`
                    );
                  }

                  // Store conversation in SQL (messages + metadata as JSONB)
                  try {
                    const firstTaskId = results.find((r: any) => r?.task_id != null)?.task_id ?? null;
                    const conversationPayload = serializeConversationForStorage(this.conversation);
                    const saveResult = await sqlApi.addConversation({
                      employee_id: employee.id,
                      task_id: firstTaskId,
                      assignement_id: this.conversation.assignementId ?? null,
                      conversation: conversationPayload,
                    });
                    if (saveResult?.conversation_id != null) {
                      this.conversation.savedConversationId = saveResult.conversation_id;
                    }
                  } catch (convErr) {
                    console.warn('Failed to save conversation to database:', convErr);
                  }
                }
              } catch (error) {
                const lang = this.getCurrentLanguage();
                this.conversation.addSystemMessage(
                  lang === 'nl' 
                    ? "Fout bij het opslaan van taken. Probeer het opnieuw."
                    : "Error saving tasks. Please try again."
                );
              }
            } else if (this.currentUser && confirmedTaskData.length === 0) {
              // No tasks to submit but user is logged in: still save conversation
              try {
                const employees = await sqlApi.getAllEmployees();
                const usernameParts = this.currentUser.split('_');
                const employee = employees.find((emp: any) => 
                  emp.name.toLowerCase() === usernameParts[0]?.toLowerCase() &&
                  emp.surname.toLowerCase() === usernameParts.slice(1).join('_').toLowerCase()
                );
                if (employee) {
                  const conversationPayload = serializeConversationForStorage(this.conversation);
                  const saveResult = await sqlApi.addConversation({
                    employee_id: employee.id,
                    task_id: null,
                    assignement_id: this.conversation.assignementId ?? null,
                    conversation: conversationPayload,
                  });
                  if (saveResult?.conversation_id != null) {
                    this.conversation.savedConversationId = saveResult.conversation_id;
                  }
                }
              } catch (convErr) {
                console.warn('Failed to save conversation to database:', convErr);
              }
            }
          }
          
          // Step 3: Update conversation dialogue state to conv_bye (if not already)
          // Note: The reducer should already emit UPDATE_STATE effect, but we ensure it here
          if (this.conversation.conversationDialogueState !== 'conv_bye') {
            this.conversation.conversationDialogueState = 'conv_bye';
          }
          
          // Step 4: Mark that we've processed submission for this conversation
          // This prevents duplicate triggers
          if (!this.conversation._submissionProcessed) {
            this.conversation._submissionProcessed = true;
          }
          
          // Step 5: Only trigger new conversation if this was a FRESH submission
          // (not an old conversation being loaded)
          // We only check wasAlreadySubmitted - if it was false, this is a fresh submission
          this.triggerConversationUpdate();
          
          const callback = this.onNewConversationRequested;
          if (callback && !wasAlreadySubmitted) {
            // This is a fresh submission - trigger new conversation after a delay
            setTimeout(() => {
              // Double-check that conversation is still submitted before triggering
              if (this.conversation?.isSubmitted && this.conversation?._submissionProcessed && callback) {
                callback();
                // Clear the flag after triggering to allow re-trigger if needed
                if (this.conversation) {
                  this.conversation._submissionProcessed = false;
                }
              }
            }, 1500);
          }
        } else {
          this.triggerConversationUpdate();
        }
        break;
        
      case 'show_defined_task_message':
        // Show message that user selected a predefined task
        const lang = this.getCurrentLanguage();
        
        this.conversation.addSystemMessage(t(lang, 'definedTaskSelected'), 'show_defined_task_message');
        
        // Add instruction message
        this.conversation.addSystemMessage(t(lang, 'subtaskSelectionInstruction'), 'subtask_selection_instruction');
        
        // Get wellDefinedTaskId from params (passed from reducer) or from any task with wellDefinedTaskId
        const wellDefinedTaskId = params?.wellDefinedTaskId || 
          this.conversation.tasks?.find((t: any) => t.wellDefinedTaskId)?.wellDefinedTaskId;
        
        if (wellDefinedTaskId) {
          // Get subtasks for this well-defined task
          const { getSubtasksForTask } = await import('../DialogueManagementDefinedTaskClassification/wellDefinedTasks');
          const subtasks = getSubtasksForTask(wellDefinedTaskId, lang);
          
          // Create subtask selection message
          const subtaskMessage = {
            id: Math.random().toString(36).substr(2, 9),
            text: t(lang, 'selectSubtasksLabel'),
            sender: 'Assistant',
            timestamp: new Date(),
            isAssistant: true,
            messageType: 'subtaskSelection' as any,
            subtasks: subtasks.map(st => ({ id: st.id, name: st.name, description: st.description }))
          };
          this.conversation.messages.push(subtaskMessage);
        }
        
        this.triggerConversationUpdate();
        break;
        
      case 'show_well_defined_tasks_list':
        // Show "The well defined tasks are:" and let MainChat show the task buttons (WellDefinedTaskSuggestions)
        const langWellDefined = this.getCurrentLanguage();
        const message = t(langWellDefined, 'wellDefinedTasksAre');
        this.conversation.addSystemMessage(message, 'well_defined_tasks_explanation');
        this.triggerConversationUpdate();
        break;
        
        
      case 'ask_subtask_amount':
        // Ask user for amount for the subtask
        // Note: amountSubtaskId might be a variant ID if a variant was selected
        const amountSubtaskId = params?.subtaskId;
        if (!amountSubtaskId) {
          break;
        }
        
        const langAmount = this.getCurrentLanguage();
        
        // Try to get unit from processed subtasks first
        const activeTaskForSubtaskAmount = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
        const subtaskDataForAmount = activeTaskForSubtaskAmount?.subtaskData || activeTaskForSubtaskAmount?.subtasks;
        let subtaskUnit = '';
        
        // FIRST: Try to get unit directly from the variant object itself (since backend now includes billing info)
        // Find the variant message that contains this variant
        const variantMessageForUnit = this.conversation.messages
          .slice()
          .reverse()
          .find((msg: any) => msg.messageType === 'variantSelection' && msg.variants && msg.variants[amountSubtaskId]);
        
        if (variantMessageForUnit && variantMessageForUnit.variants && variantMessageForUnit.variants[amountSubtaskId]) {
          const variantObj = variantMessageForUnit.variants[amountSubtaskId];
          if (variantObj && typeof variantObj === 'object' && 'billable_unit' in variantObj) {
            subtaskUnit = variantObj.billable_unit || '';
          }
        }
        
        // SECOND: Try to get unit from parent task's billing info (like regular task management)
        // This is the same approach as regular task management - use selectedTask.billing[variantId]
        if (!subtaskUnit && activeTaskForSubtaskAmount?.selectedTask?.billing && amountSubtaskId) {
          // Check if amountSubtaskId is a variant ID in the billing object
          if (activeTaskForSubtaskAmount.selectedTask.billing[amountSubtaskId]) {
            subtaskUnit = activeTaskForSubtaskAmount.selectedTask.billing[amountSubtaskId].billable_unit || '';
          }
        }
        
        // SECOND: Check if this ID is a variant ID in any processed subtask
        if (!subtaskUnit && subtaskDataForAmount?.processedSubtasks) {
          // First, try to find by variantId (in case amountSubtaskId is a variant)
          const processedSubtaskWithVariant = subtaskDataForAmount.processedSubtasks.find(
            (s: any) => s.variantId === amountSubtaskId
          );
          if (processedSubtaskWithVariant?.unit) {
            subtaskUnit = processedSubtaskWithVariant.unit;
          } else {
            // Fallback: try to find by subtaskId
            const processedSubtask = subtaskDataForAmount.processedSubtasks.find(
              (s: any) => s.subtaskId === amountSubtaskId
            );
            if (processedSubtask?.unit) {
              subtaskUnit = processedSubtask.unit;
            }
          }
        }
        
        // If unit not found, fetch from backend
        if (!subtaskUnit) {
          try {
            // Check if we need to find the parent task ID (if amountSubtaskId is a variant ID)
            // Variant IDs are typically longer (e.g., 182020) and are keys in the parent's billing object
            let taskIdToFetch = amountSubtaskId;
            let isVariantId = false;
            
            // Check if this is a variant ID by looking at processed subtasks
            if (subtaskDataForAmount?.processedSubtasks) {
              const subtaskWithVariant = subtaskDataForAmount.processedSubtasks.find(
                (s: any) => s.variantId === amountSubtaskId
              );
              if (subtaskWithVariant) {
                // This is a variant ID, we need to find the parent task
                // The parent should be in currentChildId (set when we auto-select a child)
                // or we can use the subtaskId from the entry
                const parentTaskId = subtaskDataForAmount.currentChildId || subtaskWithVariant.childId || subtaskWithVariant.subtaskId;
                
                if (parentTaskId && parentTaskId !== amountSubtaskId) {
                  taskIdToFetch = parentTaskId;
                  isVariantId = true;
                }
              }
            }
            
            // Backend expects task_id as int, so try to parse it
            // If it's a variant ID or contains non-numeric characters, we need to find the numeric parent
            let taskIdForRequest: number;
            const parsedInt = parseInt(String(taskIdToFetch));
            
            if (!isNaN(parsedInt)) {
              taskIdForRequest = parsedInt;
            } else {
              // If we can't parse it, try to extract numeric part or use a fallback
              // For IDs like "1820XX", try to find the numeric parent
              // Try to extract numeric prefix (e.g., "1820" from "1820XX")
              const numericMatch = String(taskIdToFetch).match(/^(\d+)/);
              if (numericMatch) {
                taskIdForRequest = parseInt(numericMatch[1]);
              } else {
                // Don't make the request if we can't get a valid integer
                taskIdForRequest = 0; // This will likely fail, but we'll handle the error
              }
            }
            
            const { API_BASE_URL } = await import('../../../../services/apiConfig');
            const response = await fetch(`${API_BASE_URL}/task_by_id`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                task_id: taskIdForRequest,
                language: langAmount
              })
            });
            
            if (response.ok) {
              const result = await response.json();
              
              // The backend returns: { parent_task: {...}, children: [...], total_children_count: N }
              // We need to check if the subtask ID matches the parent_task or one of the children
              
              // Check parent_task first
              if (result.parent_task) {
                if (result.parent_task.task_id === amountSubtaskId || result.parent_task.task_id === String(amountSubtaskId)) {
                  // This is the parent task
                  if (result.parent_task.billing) {
                    // billing is a dict like { "task_id": { "billable_unit": "...", ... } }
                    const billingKeys = Object.keys(result.parent_task.billing);
                    if (billingKeys.length > 0) {
                      const firstBillingKey = billingKeys[0];
                      const billingInfo = result.parent_task.billing[firstBillingKey];
                      subtaskUnit = billingInfo?.billable_unit || '';
                    }
                  }
                }
              }
              
              // If this is a variant ID, look for it in the billing objects
              if (isVariantId) {
                // Check parent_task billing first
                if (result.parent_task?.billing && result.parent_task.billing[amountSubtaskId]) {
                  subtaskUnit = result.parent_task.billing[amountSubtaskId].billable_unit || '';
                }
                
                // Check children billing
                if (!subtaskUnit && result.children && Array.isArray(result.children)) {
                  for (const child of result.children) {
                    if (child.billing && child.billing[amountSubtaskId]) {
                      subtaskUnit = child.billing[amountSubtaskId].billable_unit || '';
                      break;
                    }
                  }
                }
              }
              
              // If not found in parent, check children (for non-variant IDs)
              if (!subtaskUnit && result.children && Array.isArray(result.children)) {
                for (const child of result.children) {
                  // Check if this child matches the subtask ID (exact match or starts with)
                  const childMatches = child.task_id === amountSubtaskId || 
                                      child.task_id === String(amountSubtaskId) ||
                                      String(child.task_id).startsWith(String(amountSubtaskId)) ||
                                      String(amountSubtaskId).startsWith(String(child.task_id));
                  
                  if (childMatches) {
                    // Found the matching child
                    if (child.billing) {
                      const billingKeys = Object.keys(child.billing);
                      if (billingKeys.length > 0) {
                        // Try to get billing info from any key (variants might have different IDs)
                        // Get the first billing entry that has a billable_unit
                        for (const billingKey of billingKeys) {
                          const billingInfo = child.billing[billingKey];
                          if (billingInfo?.billable_unit) {
                            subtaskUnit = billingInfo.billable_unit;
                            break;
                          }
                        }
                        if (subtaskUnit) break;
                      }
                    }
                  }
                }
              }
              
              // If still not found, check if any child has billing info (for cases where parent ID doesn't match)
              if (!subtaskUnit && result.children && Array.isArray(result.children) && result.children.length > 0) {
                const firstChild = result.children[0];
                if (firstChild.billing) {
                  const billingKeys = Object.keys(firstChild.billing);
                  if (billingKeys.length > 0) {
                    // If we're looking for a variant ID, try to find it in the billing keys
                    if (isVariantId && firstChild.billing[amountSubtaskId]) {
                      subtaskUnit = firstChild.billing[amountSubtaskId].billable_unit || '';
                    } else {
                      // Get billing from first variant
                      const firstBillingKey = billingKeys[0];
                      const billingInfo = firstChild.billing[firstBillingKey];
                      subtaskUnit = billingInfo?.billable_unit || '';
                    }
                  }
                }
              }
              
              // Fallback: try direct billing access (for backwards compatibility)
              if (!subtaskUnit && result.billing) {
                if (result.billing[amountSubtaskId]) {
                  subtaskUnit = result.billing[amountSubtaskId].billable_unit || '';
                } else if (typeof result.billing === 'object') {
                  const billingKeys = Object.keys(result.billing);
                  if (billingKeys.length > 0) {
                    const firstBilling = result.billing[billingKeys[0]];
                    subtaskUnit = firstBilling?.billable_unit || '';
                  }
                }
              }
              
              // Store the unit in the processed subtask data for future use
              // IMPORTANT: Store in BOTH subtaskData.processedSubtasks (for context) AND processedSubtasks (for task object)
              // CRITICAL: Find entries by the ORIGINAL subtaskId (from currentSubtaskIndex), not by amountSubtaskId
              // amountSubtaskId might be a variant ID, but we need to update the entry with the original subtaskId
              if (subtaskUnit && activeTaskForSubtaskAmount) {
                // Get the original subtask ID from the current index
                const originalSubtaskId = activeTaskForSubtaskAmount.selectedSubtasks?.[activeTaskForSubtaskAmount.currentSubtaskIndex || 0];
                
                if (originalSubtaskId) {
                  // Store in subtaskData.processedSubtasks (for context/reducer)
                  if (!activeTaskForSubtaskAmount.subtaskData) {
                    activeTaskForSubtaskAmount.subtaskData = { processedSubtasks: [] };
                  }
                  if (!activeTaskForSubtaskAmount.subtaskData.processedSubtasks) {
                    activeTaskForSubtaskAmount.subtaskData.processedSubtasks = [];
                  }
                  
                  // Find entry by original subtaskId (should always exist)
                  let processedSubtask = activeTaskForSubtaskAmount.subtaskData.processedSubtasks.find(
                    (s: any) => s.subtaskId === originalSubtaskId
                  );
                  if (!processedSubtask) {
                    processedSubtask = { subtaskId: originalSubtaskId };
                    activeTaskForSubtaskAmount.subtaskData.processedSubtasks.push(processedSubtask);
                  }
                  processedSubtask.unit = subtaskUnit;
                  
                  // ALSO store in task.processedSubtasks (for task summary)
                  if (!activeTaskForSubtaskAmount.processedSubtasks) {
                    activeTaskForSubtaskAmount.processedSubtasks = [];
                  }
                  
                  // Find entry by original subtaskId (should always exist)
                  let taskProcessedSubtask = activeTaskForSubtaskAmount.processedSubtasks.find(
                    (s: any) => s.subtaskId === originalSubtaskId
                  );
                  if (!taskProcessedSubtask) {
                    taskProcessedSubtask = { subtaskId: originalSubtaskId };
                    activeTaskForSubtaskAmount.processedSubtasks.push(taskProcessedSubtask);
                  }
                  taskProcessedSubtask.unit = subtaskUnit;
                  
                  // CRITICAL: Also store unit directly on the task object (new architecture)
                  // This ensures the task summary can find it AND it's available when SUBTASK_AMOUNT_PROVIDED is dispatched
                  if (activeTaskForSubtaskAmount && subtaskUnit) {
                    activeTaskForSubtaskAmount.unit = subtaskUnit;
                    activeTaskForSubtaskAmount.billableUnit = subtaskUnit;
                    // Trigger update to ensure the unit is persisted
                    this.triggerConversationUpdate();
                  }
                }
              }
            }
          } catch (error) {
            // Error fetching unit from backend
          }
        } else {
          // If we found a cached unit, also ensure it's stored in task.processedSubtasks
          if (subtaskUnit && activeTaskForSubtaskAmount) {
            if (!activeTaskForSubtaskAmount.processedSubtasks) {
              activeTaskForSubtaskAmount.processedSubtasks = [];
            }
            
            // Find or create entry in task.processedSubtasks
            let taskProcessedSubtask = activeTaskForSubtaskAmount.processedSubtasks.find(
              (s: any) => s.subtaskId === amountSubtaskId || s.variantId === amountSubtaskId
            );
            if (!taskProcessedSubtask) {
              taskProcessedSubtask = { subtaskId: amountSubtaskId };
              activeTaskForSubtaskAmount.processedSubtasks.push(taskProcessedSubtask);
            }
            if (!taskProcessedSubtask.unit) {
              taskProcessedSubtask.unit = subtaskUnit;
            }
          }
        }
        
        // CRITICAL: ALWAYS store unit on the task object if we have it (whether from cache or backend)
        // This ensures it's available when SUBTASK_AMOUNT_PROVIDED is dispatched
        if (subtaskUnit && activeTaskForSubtaskAmount) {
          activeTaskForSubtaskAmount.unit = subtaskUnit;
          activeTaskForSubtaskAmount.billableUnit = subtaskUnit;
        }
        
        // Display unit or placeholder
        const unitDisplay = subtaskUnit || 'N/A';
        const langForAmount = this.getCurrentLanguage();
        this.conversation.addSystemMessage(
          t(langForAmount, 'pleaseProvideAmountForTask', { unit: unitDisplay }),
          'ask_subtask_amount'
        );
        this.triggerConversationUpdate();
        break;
        
      case 'show_subtask_variant_options':
        // Show variant options for the subtask
        const variantSubtaskId = params?.subtaskId;
        const subtaskVariants = params?.variants;
        const parentTaskForVariants = params?.parentTask; // Parent task object with billing info
        
        if (!variantSubtaskId || !subtaskVariants) {
          break;
        }
        
        // Store the parent task object in the active task for later use (similar to regular task management)
        const activeTaskForVariants = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
        if (activeTaskForVariants && parentTaskForVariants) {
          // Store the parent task as selectedTask so we can access its billing info later
          activeTaskForVariants.selectedTask = parentTaskForVariants;
          activeTaskForVariants.selectedTaskId = variantSubtaskId;
        }
        
        // Get task description/name for display from parent task object
        // The parent task object should have the description from the backend
        let taskDescription = '';
        if (parentTaskForVariants) {
          // Try different possible fields for description
          taskDescription = parentTaskForVariants.description || 
                           parentTaskForVariants.task_description || 
                           parentTaskForVariants.dutch_description || 
                           '';
        }
        
        // If not found in parent task, try to get from well-defined tasks structure
        if (!taskDescription) {
          const activeTaskForVariants = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
          if (activeTaskForVariants?.wellDefinedTaskId) {
            try {
              const { getSubtasksForTask } = await import('../DialogueManagementDefinedTaskClassification/wellDefinedTasks');
              const lang = this.getCurrentLanguage();
              const subtasks = getSubtasksForTask(activeTaskForVariants.wellDefinedTaskId, lang);
              const currentSubtask = subtasks.find((s: any) => s.id === variantSubtaskId);
              if (currentSubtask) {
                taskDescription = currentSubtask.name || currentSubtask.description || '';
              }
            } catch (err) {
              // Could not get subtask description from well-defined tasks
            }
          }
        }
        
        // Build the message text with task name if available
        const langForVariant = this.getCurrentLanguage();
        const variantMessageText = taskDescription 
          ? t(langForVariant, 'pleaseSelectVariantForTask', { taskDescription })
          : t(langForVariant, 'pleaseSelectVariantForSubtask', { subtaskId: variantSubtaskId });
        
        // VariantSelectorMessage expects 'variants' as an object (not variantOptions array)
        // The object structure should be: { "variantId": { ...variantData }, ... }
        // Use the variants object directly, similar to how ask_variant does it
        const variantMessage = {
          id: Math.random().toString(36).substr(2, 9),
          text: variantMessageText,
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'variantSelection' as any,
          variants: subtaskVariants,  // Use 'variants' (object) not 'variantOptions' (array)
          taskId: variantSubtaskId,  // Store the parent task ID for reference
          billing: parentTaskForVariants?.billing  // Store billing info for variant lookup
        };
        this.conversation.messages.push(variantMessage);
        this.triggerConversationUpdate();
        break;
        
      case 'show_subtask_children_options':
        // Show children options for the subtask
        const childrenSubtaskId = params?.subtaskId;
        const children = params?.children;
        
        if (!childrenSubtaskId || !children || children.length === 0) {
          break;
        }
        
        // Get the subtask description/name for display
        // Try multiple sources: well-defined tasks, active task object, or API
        let subtaskDescription = '';
        
        // First, try to get from active task object
        const activeTaskForChildren = this.conversation.tasks?.find((t: any) => t.isProcessingDialogue);
        if (activeTaskForChildren) {
          // Try userDescription or description from task object
          subtaskDescription = activeTaskForChildren.userDescription || 
                              activeTaskForChildren.description || 
                              activeTaskForChildren.taskDescription || 
                              '';
          
          // If not found and we have a well-defined task, try that
          if (!subtaskDescription && activeTaskForChildren.wellDefinedTaskId) {
            try {
              const { getSubtasksForTask } = await import('../DialogueManagementDefinedTaskClassification/wellDefinedTasks');
              const lang = this.getCurrentLanguage();
              const subtasks = getSubtasksForTask(activeTaskForChildren.wellDefinedTaskId, lang);
              const currentSubtask = subtasks.find((s: any) => s.id === childrenSubtaskId);
              if (currentSubtask) {
                subtaskDescription = currentSubtask.name || currentSubtask.description || '';
              }
            } catch (err) {
            }
          }
          
          // If still not found, try to fetch from API using taskId
          if (!subtaskDescription && activeTaskForChildren.taskId) {
            try {
              const { API_BASE_URL } = await import('../../../../services/apiConfig');
              const lang = this.getCurrentLanguage();
              const taskIdForRequest = parseInt(String(activeTaskForChildren.taskId));
              if (!isNaN(taskIdForRequest)) {
                const response = await fetch(`${API_BASE_URL}/task_by_id`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    task_id: taskIdForRequest,
                    language: lang
                  })
                });
                
                if (response.ok) {
                  const result = await response.json();
                  if (result.parent_task) {
                    subtaskDescription = result.parent_task.description || 
                                        result.parent_task.dutch_description || 
                                        '';
                  }
                }
              }
            } catch (err) {
            }
          }
        }
        
        // Create children selection messages (one per child, similar to task selection format)
        // The TaskSuggestions component expects messages with taskDescription and parentId
        const parentMessageId = Math.random().toString(36).substr(2, 9);
        
        const langForChildren = this.getCurrentLanguage(); // Global language setting
        const childMessages: any[] = [];
        
        children.forEach((child: any, index: number) => {
          // Get the correct description based on GLOBAL language setting
          // Tasks don't have their own language - they use the global user language
          const childId = child.id || child.task_id || `child-${index}`;
          const hasDutchDescription = 'dutch_description' in child;
          const hasDescription = 'description' in child;
          const dutchDescValue = child.dutch_description;
          const descValue = child.description;
          
          // Comprehensive logging for children
          console.log('🔍 [EntryActionsHandler] Child Description Selection:', {
            childId,
            parentSubtaskId: childrenSubtaskId,
            index,
            globalLanguage: langForChildren,
            hasDutchDescription,
            hasDescription,
            dutchDescriptionValue: dutchDescValue,
            descriptionValue: descValue,
            dutchDescriptionType: typeof dutchDescValue,
            descriptionType: typeof descValue,
            dutchDescriptionLength: dutchDescValue ? dutchDescValue.length : 0,
            descriptionLength: descValue ? descValue.length : 0,
            fullChildObject: {
              id: child.id,
              task_id: child.task_id,
              description: child.description,
              dutch_description: child.dutch_description,
              parent_id: child.parent_id,
              similarity: child.similarity
            }
          });
          
          // Use global language setting to get correct description
          const childDescription = langForChildren === 'nl' 
            ? (child.dutch_description || child.description || '')
            : (child.description || '');
          
          console.log('✅ [EntryActionsHandler] Selected child description:', {
            childId,
            globalLanguage: langForChildren,
            usedField: langForChildren === 'nl' 
              ? (child.dutch_description ? 'dutch_description' : (child.description ? 'description' : 'empty'))
              : (child.description ? 'description' : 'empty'),
            finalDescription: childDescription,
            finalDescriptionLength: childDescription.length
          });
          
          const childMessage = {
            id: `child-${childrenSubtaskId}-${index}`,
            text: t(langForChildren, 'taskIdDescription', { taskId: child.id, description: childDescription }),
            sender: 'AI Assistant',
            timestamp: new Date(),
            isAssistant: true,
            messageType: 'taskSelection' as any,
            parentId: parentMessageId,
            taskDescription: childDescription,
            taskId: child.id
          };
          childMessages.push(childMessage);
        });
        
        // Add a prompt message before the children options with subtask description
        const promptMessageText = subtaskDescription
          ? t(langForChildren, 'pleaseSelectMostFittingDescription', { subtaskDescription })
          : t(langForChildren, 'pleaseSelectMostFittingDescriptionForSubtask', { subtaskId: childrenSubtaskId });
        
        const promptMessage = {
          id: parentMessageId,
          text: promptMessageText,
          sender: 'Assistant',
          timestamp: new Date(),
          isAssistant: true,
          messageType: 'assistant' as any
        };
        
        // Add prompt message first, then children messages
        this.conversation.messages.push(promptMessage);
        childMessages.forEach(msg => this.conversation.messages.push(msg));
        
        
        this.triggerConversationUpdate();
        break;
        
      default:
        // Unknown entry action key
    }
  }
}

